/**
 * Wohlbekannte ID des Default-Tenants (Spiegel von `DEFAULT_TENANT_ID` aus
 * `@callbert-ai/shared`): bündelt alle direkt von Callbert verwalteten Kunden.
 */
export const DEFAULT_TENANT_ID = '00000000-0000-0000-0000-000000000001';

/** localStorage-Key der globalen Tenant-Auswahl im Admin-Dashboard. */
export const TENANT_SELECTION_STORAGE_KEY = 'admin-selected-tenant';

/**
 * Global ausgewählter Tenant für Super-Admins. Es gibt bewusst keine
 * "Alle Tenants"-Auswahl mehr — es ist immer genau ein Tenant aktiv
 * (Default: {@link DIRECT_CUSTOMERS_SELECTION}).
 */
export interface TenantSelection {
  /** Tenant-ID; `DEFAULT_TENANT_ID` = direkt verwaltete Kunden. */
  id: string;
  /** Anzeigename für den Header. */
  name: string;
}

/** Auswahl „Direkt verwaltete Kunden" (Default-Tenant). Fallback, wenn keine/keine gültige Auswahl gespeichert ist. */
export const DIRECT_CUSTOMERS_SELECTION: TenantSelection = {
  id: DEFAULT_TENANT_ID,
  name: 'Direkt verwaltete Kunden',
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Liest eine gespeicherte Auswahl defensiv ein (localStorage ist untrusted).
 * @param raw Roh-Wert aus dem localStorage.
 * @returns Gültige Auswahl oder null bei fehlendem/kaputtem Wert (Aufrufer entscheidet über Fallback).
 */
export function parseTenantSelection(raw: string | null): TenantSelection | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const candidate = parsed as Record<string, unknown>;
  if (typeof candidate.id !== 'string' || !UUID_PATTERN.test(candidate.id)) return null;
  if (typeof candidate.name !== 'string' || candidate.name.length === 0) return null;
  return { id: candidate.id, name: candidate.name };
}

/**
 * Liefert die initiale Tenant-Auswahl für Super-Admins — es ist NIE null:
 * fehlt/ist der gespeicherte Wert kaputt, greift der Default-Tenant.
 * @param raw Roh-Wert aus dem localStorage.
 * @returns Gespeicherte Auswahl oder {@link DIRECT_CUSTOMERS_SELECTION} als Fallback.
 */
export function resolveInitialTenantSelection(raw: string | null): TenantSelection {
  return parseTenantSelection(raw) ?? DIRECT_CUSTOMERS_SELECTION;
}

/**
 * Prüft, ob eine gespeicherte Auswahl auf einen mittlerweile nicht mehr
 * existierenden Tenant zeigt (z.B. gelöscht). Der Default-Tenant gilt nie
 * als veraltet, da er nicht Teil der ladbaren Tenant-Liste ist.
 * @param selection Aktuell aktive Auswahl.
 * @param matchingTenants Treffer einer Suche nach `selection.id` (leer = nicht gefunden).
 * @returns true, wenn die Auswahl auf einen Fallback zurückfallen sollte.
 */
export function isSelectionStale(selection: TenantSelection, matchingTenants: { id: string }[]): boolean {
  if (selection.id === DIRECT_CUSTOMERS_SELECTION.id) return false;
  return !matchingTenants.some((tenant) => tenant.id === selection.id);
}
